import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Flag, VolumeX, Volume2, Ban, AlertTriangle, LogOut } from 'lucide-react';

export default function ModerationPanel({ onClose, roomUsers, isCreator, mutedUsers, onMute, onUnmute, onKick, onBan, currentUserId }) {
  const { t } = useLanguage();
  const [reportReason, setReportReason] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [actionDone, setActionDone] = useState('');

  const showAction = (msg) => {
    setActionDone(msg);
    setTimeout(() => setActionDone(''), 3000);
  };

  const otherUsers = roomUsers?.filter(u => u.id !== currentUserId) || [];

  return (
    <div data-testid="moderation-panel" className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="glass-card rounded-3xl p-8 max-w-md w-full mx-4 relative">
        <button data-testid="close-moderation-btn" onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-all">
          <X className="w-5 h-5" />
        </button>

        <h3 className="font-display text-xl font-bold mb-6 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-[#FF007F]" />
          {isCreator ? 'Room Management' : 'Report'}
        </h3>

        {otherUsers.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-4">No other users in the room.</p>
        ) : (
          <>
            <div className="mb-4">
              <label className="text-xs text-slate-400 mb-1 block">Select User</label>
              <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none">
                <option value="">Choose a user...</option>
                {otherUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.display_name} {mutedUsers?.includes(u.id) ? '(muted)' : ''}</option>
                ))}
              </select>
            </div>

            {isCreator && selectedUser && (
              <>
                <p className="text-xs text-slate-400 mb-2">Creator Actions</p>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {mutedUsers?.includes(selectedUser) ? (
                    <button data-testid="unmute-user-btn" onClick={() => { onUnmute(selectedUser); showAction('User unmuted'); }}
                      className="py-3 rounded-xl bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all text-sm font-medium flex items-center justify-center gap-2">
                      <Volume2 className="w-4 h-4" /> Unmute
                    </button>
                  ) : (
                    <button data-testid="mute-user-btn" onClick={() => { onMute(selectedUser); showAction('User muted'); }}
                      className="py-3 rounded-xl bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all text-sm font-medium flex items-center justify-center gap-2">
                      <VolumeX className="w-4 h-4" /> {t('mute')}
                    </button>
                  )}
                  <button data-testid="kick-user-btn" onClick={() => { onKick(selectedUser); showAction('User kicked'); }}
                    className="py-3 rounded-xl bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 transition-all text-sm font-medium flex items-center justify-center gap-2">
                    <LogOut className="w-4 h-4" /> Kick
                  </button>
                  <button data-testid="ban-user-btn" onClick={() => { onBan(selectedUser); showAction('User banned permanently'); }}
                    className="col-span-2 py-3 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all text-sm font-medium flex items-center justify-center gap-2">
                    <Ban className="w-4 h-4" /> Ban Permanently
                  </button>
                </div>
              </>
            )}

            <div className="mb-4">
              <label className="text-xs text-slate-400 mb-1 block">Report reason</label>
              <textarea value={reportReason} onChange={e => setReportReason(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none resize-none h-20"
                placeholder="Describe the issue..." />
            </div>
            <button data-testid="report-btn" onClick={() => showAction('Report sent')}
              className="w-full py-3 rounded-xl bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-all text-sm font-medium flex items-center justify-center gap-2">
              <Flag className="w-4 h-4" /> {t('report')}
            </button>
          </>
        )}

        {actionDone && (
          <div className="mt-4 p-3 rounded-xl bg-emerald-500/10 text-emerald-400 text-sm text-center">{actionDone}</div>
        )}
      </div>
    </div>
  );
}
